import React from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { useCMS } from '../context/CMSContext';

const Initiatives = () => {
  const { content, getPageData } = useCMS();
  const pageData = getPageData('/initiatives');

  const c = (key, fallback) => {
    if (pageData && pageData.sectionData) {
      const sections = ['header', 'program', 'cta'];
      for (const type of sections) {
        if (key.includes(`_${type}_`)) {
          const field = key.split(`_${type}_`)[1];
          const val = pageData.sectionData[type]?.[field];
          if (val) return val;
        }
      }
    }
    return content[key] || fallback;
  };

  return (
    <div className="animate-in fade-in duration-700 bg-brandBeige">
      
      {/* Header */}
      <section className="pt-0 pb-8 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/eunikare.jpeg"
            alt="Eunikare"
            className="w-full h-[250px] sm:h-[350px] md:h-[450px] lg:h-[500px] object-cover rounded-[3rem] shadow-lg"
          />
        </div>
      </section>

      {/* Grid Iteration */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            
            {/* Initiative 1 (Featured - spans 2 cols) */}
            <div className="bg-brandWhite rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col group md:col-span-2 md:flex-row">
              <div className="relative overflow-hidden h-64 md:h-auto md:w-1/2">
                <img className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Mikka Parents School" src="/images/school.png" />
                <div className="absolute top-8 left-8 w-16 h-16 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl">🎓</div>
              </div>
              <div className="p-10 flex flex-col justify-center flex-1">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-brandGreen transition-colors">{c('initiatives_program_title_1', 'Mikka Parents School')}</h3>
                <p className="text-brandGreen font-bold text-sm uppercase tracking-widest mb-4">{c('initiatives_program_tag_1', 'Educating vulnerable children for a brighter and dignified future')}</p>
                <p className="text-brandBlack/60 mb-8 leading-relaxed line-clamp-3">
                  {c('initiatives_program_desc_1', 'An education-focused initiative designed to provide holistic, quality learning opportunities for vulnerable children, including those from disadvantaged and refugee backgrounds.')}
                </p>
                <div className="flex items-center space-x-4">
                  <button className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold hover:bg-brandGreen transition-all flex items-center shadow-lg">
                    Learn More <ChevronRight className="ml-1 w-4 h-4" />
                  </button>
                  <button className="text-brandBlack/40 hover:text-brandBlack transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Initiative 2 */}
            <div className="bg-brandWhite rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col group">
              <div className="relative overflow-hidden h-64">
                <img className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Agriscale Initiative" src="/images/Agris.png" />
                <div className="absolute top-8 left-8 w-16 h-16 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl">🌱</div>
              </div>
              <div className="p-10 flex flex-col justify-center flex-1">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-brandGreen transition-colors">{c('initiatives_program_title_2', 'Agriscale Initiative')}</h3>
                <p className="text-brandGreen font-bold text-sm uppercase tracking-widest mb-4">{c('initiatives_program_tag_2', 'Empowering women and girls to grow sustainable livelihoods')}</p>
                <p className="text-brandBlack/60 mb-8 leading-relaxed line-clamp-3">
                  {c('initiatives_program_desc_2', 'Designed for empowering women and girls with smart farming skills, agribusiness knowledge, and market access to strengthen livelihoods.')}
                </p>
                <div className="flex items-center space-x-4">
                  <button className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold hover:bg-brandGreen transition-all flex items-center shadow-lg">
                    Learn More <ChevronRight className="ml-1 w-4 h-4" />
                  </button>
                  <button className="text-brandBlack/40 hover:text-brandBlack transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Initiative 3 */}
            <div className="bg-brandWhite rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col group">
              <div className="relative overflow-hidden h-64">
                <img className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Collectively Christians" src="/images/christian.png" />
                <div className="absolute top-8 left-8 w-16 h-16 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl">✝️</div>
              </div>
              <div className="p-10 flex flex-col justify-center flex-1">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-brandGreen transition-colors">{c('initiatives_program_title_3', 'CollectivelyChristians.com')}</h3>
                <p className="text-brandGreen font-bold text-sm uppercase tracking-widest mb-4">{c('initiatives_program_tag_3', 'Building empowered, self-reliant Christian communities')}</p>
                <p className="text-brandBlack/60 mb-8 leading-relaxed line-clamp-3">
                  {c('initiatives_program_desc_3', 'A social enterprise dedicated to empowering Christian communities through skills development, economic initiatives, and collaborative events.')}
                </p>
                <div className="flex items-center space-x-4">
                  <button className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold hover:bg-brandGreen transition-all flex items-center shadow-lg">
                    Learn More <ChevronRight className="ml-1 w-4 h-4" />
                  </button>
                  <button className="text-brandBlack/40 hover:text-brandBlack transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Initiative 4 (Featured - spans 2 cols) */}
            <div className="bg-brandWhite rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col group md:col-span-2 md:flex-row">
              <div className="relative overflow-hidden h-64 md:h-auto md:w-1/2">
                <img className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Kilimani Innovation Hub" src="/images/kilimani.png" />
                <div className="absolute top-8 left-8 w-16 h-16 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl">💡</div>
              </div>
              <div className="p-10 flex flex-col justify-center flex-1">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-brandGreen transition-colors">{c('initiatives_program_title_4', 'Kilimani Innovation Hub')}</h3>
                <p className="text-brandGreen font-bold text-sm uppercase tracking-widest mb-4">{c('initiatives_program_tag_4', 'Shaping young innovators to create solutions that matter')}</p>
                <p className="text-brandBlack/60 mb-8 leading-relaxed line-clamp-3">
                  {c('initiatives_program_desc_4', 'An innovation and entrepreneurship hub nurturing visionary young entrepreneurs by providing mentorship, skills training, and a supportive environment.')}
                </p>
                <div className="flex items-center space-x-4">
                  <button className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold hover:bg-brandGreen transition-all flex items-center shadow-lg">
                    Learn More <ChevronRight className="ml-1 w-4 h-4" />
                  </button>
                  <button className="text-brandBlack/40 hover:text-brandBlack transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

        
            {/* Initiative 6 */}
            <div className="bg-brandWhite rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col md:flex-row group md:col-span-2">
              <div className="relative overflow-hidden h-64 md:h-auto md:w-1/2">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  alt="Cycle of Care"
                  src="/images/cycle.png"
                />
                <div className="absolute top-8 left-8 w-16 h-16 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl">
                  🩺
                </div>
              </div>

              <div className="p-10 flex flex-col justify-center flex-1">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-brandGreen transition-colors">
                  {c('initiatives_program_title_6', 'Cycle of Care')}
                </h3>

                <p className="text-brandGreen font-bold text-sm uppercase tracking-widest mb-4">
                  {c(
                    'initiatives_program_tag_6',
                    'Restoring dignity, protecting health, and empowering girls'
                  )}
                </p>

                <p className="text-brandBlack/60 mb-8 leading-relaxed line-clamp-3">
                  {c(
                    'initiatives_program_desc_6',
                    'A community health and dignity initiative focused on advancing sexual and reproductive health rights and ensuring access to menstrual health education.'
                  )}
                </p>

                <div className="flex items-center space-x-4">
                  <button className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold hover:bg-brandGreen transition-all flex items-center shadow-lg">
                    Learn More <ChevronRight className="ml-1 w-4 h-4" />
                  </button>

                  <button className="text-brandBlack/40 hover:text-brandBlack transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="py-24 bg-brandMauve/10 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6">{c('initiatives_cta_title', 'Want to support a specific initiative?')}</h2>
          <p className="text-brandBlack/60 mb-8">{c('initiatives_cta_desc', 'We welcome partners for individual projects and long-term programs.')}</p>
          <a className="bg-brandGreen text-brandWhite px-10 py-4 rounded-full font-bold hover:bg-brandBlack shadow-xl inline-block transition-all" href="#/partnership">
            Inquire About Partnerships
          </a>
        </div>
      </section>

    </div>
  );
};

export default Initiatives;
